import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Select, Spinner } from './index';
import api from '../../utils/api';

/*
 * Cascading Country → State → City picker.
 *
 * The city is what matters: every city in /geo carries coordinates, so picking
 * one is what gives a profile or a request a position to match donors around.
 * Country and state exist to narrow the list — 153k cities can't be a dropdown,
 * and city names stop being unique across countries (21 Springfields in the US
 * alone), so the parents are what make a choice unambiguous.
 *
 * Calls `onChange({ city_id, city, state, country, latitude, longitude })` — or
 * `onChange(null)` while the selection is incomplete, so a form can tell
 * "nothing picked yet" from "picked".
 */

// Countries never change mid-session and the list is ~250 rows, so it's fetched
// once per page load and shared by every picker on the page.
let countriesPromise = null;
const loadCountries = () => {
  if (!countriesPromise) {
    countriesPromise = api.get('/geo/countries')
      .then(r => r.data)
      .catch(e => { countriesPromise = null; throw e; });
  }
  return countriesPromise;
};

export default function LocationSelect({
  value,            // { country_id, state_id, city_id } to prefill
  onChange,
  disabled,
  required,
  labels = {},
}) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const [countryId, setCountryId] = useState(value?.country_id || '');
  const [stateId, setStateId] = useState(value?.state_id || '');
  const [cityId, setCityId] = useState(value?.city_id || '');

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [error, setError] = useState('');

  // Guards against a slow response for an abandoned country overwriting the
  // list for the one now selected.
  const requestSeq = useRef(0);

  useEffect(() => {
    loadCountries()
      .then(setCountries)
      .catch(() => setError('Could not load the country list. Check your connection and reload.'));
  }, []);

  useEffect(() => {
    if (!countryId) { setStates([]); return; }
    const seq = ++requestSeq.current;
    setLoadingStates(true);
    api.get('/geo/states', { params: { country_id: countryId } })
      .then(r => { if (seq === requestSeq.current) setStates(r.data); })
      .catch(() => { if (seq === requestSeq.current) setStates([]); })
      .finally(() => { if (seq === requestSeq.current) setLoadingStates(false); });
  }, [countryId]);

  useEffect(() => {
    if (!stateId) { setCities([]); return; }
    const seq = ++requestSeq.current;
    setLoadingCities(true);
    api.get('/geo/cities', { params: { state_id: stateId, limit: 500 } })
      .then(r => { if (seq === requestSeq.current) setCities(r.data); })
      .catch(() => { if (seq === requestSeq.current) setCities([]); })
      .finally(() => { if (seq === requestSeq.current) setLoadingCities(false); });
  }, [stateId]);

  const emit = useCallback((city) => {
    if (!city) { onChange(null); return; }
    const state = states.find(s => String(s.id) === String(city.state_id));
    const country = countries.find(c => String(c.id) === String(city.country_id));
    onChange({
      city_id: city.id,
      city: city.name,
      state: state?.name || null,
      country: country?.name || null,
      latitude: city.latitude,
      longitude: city.longitude,
    });
  }, [onChange, states, countries]);

  const pickCountry = (e) => {
    setCountryId(e.target.value);
    setStateId('');
    setCityId('');
    setCities([]);
    onChange(null);
  };

  const pickState = (e) => {
    setStateId(e.target.value);
    setCityId('');
    onChange(null);
  };

  const pickCity = (e) => {
    setCityId(e.target.value);
    emit(cities.find(c => String(c.id) === String(e.target.value)));
  };

  // Countries name their first-level divisions differently; using the selected
  // country's own term beats labelling a French département a "state".
  const stateLabel = labels.state
    || (states.length && states[0].type
      ? states[0].type.replace(/\b\w/g, ch => ch.toUpperCase())
      : 'State / Region');

  const star = required ? ' *' : '';

  return (
    <>
      <Select
        label={(labels.country || 'Country') + star}
        value={countryId}
        onChange={pickCountry}
        disabled={disabled || !countries.length}
      >
        <option value="">{countries.length ? 'Select a country' : 'Loading…'}</option>
        {countries.map(c => (
          <option key={c.id} value={c.id}>{c.emoji ? `${c.emoji} ` : ''}{c.name}</option>
        ))}
      </Select>

      <Select
        label={stateLabel + star}
        value={stateId}
        onChange={pickState}
        disabled={disabled || !countryId || loadingStates}
      >
        <option value="">
          {!countryId ? 'Pick a country first' : loadingStates ? 'Loading…' : 'Select'}
        </option>
        {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </Select>

      <Select
        label={(labels.city || 'City') + star}
        value={cityId}
        onChange={pickCity}
        disabled={disabled || !stateId || loadingCities}
      >
        <option value="">
          {!stateId ? 'Pick a state first' : loadingCities ? 'Loading…' : 'Select a city'}
        </option>
        {cities.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
      </Select>

      {stateId && !loadingCities && cities.length === 0 && (
        <p style={{ fontSize: '12.5px', color: 'var(--gray-500)', marginTop: '-8px', marginBottom: '14px' }}>
          No cities listed for this region yet. Pick the nearest one from another region, or share your
          GPS location instead.
        </p>
      )}
      {error && <p style={{ fontSize: '12.5px', color: 'var(--red)', marginBottom: '14px' }}>{error}</p>}
      {(loadingStates || loadingCities) && (
        <div style={{ marginTop: '-6px', marginBottom: '12px' }}><Spinner size={14} /></div>
      )}
    </>
  );
}
